import {
  AdminBulkApprovePayments,
  AdminBulkApprovePaymentsProgress,
  AdminFetchPendingPaymentDetails,
  AdminFetchPendingPayments,
  AdminReviewPayment,
} from 'src/constants/actionTypes'
import type {
  BulkApprovePaymentsPayload,
  BulkApproveProgress,
  FetchPendingPaymentDetailsPayload,
  PendingPayment,
  PendingPaymentDetails,
} from 'src/interfaces/paymentsReview'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { PaymentsReviewState } from './types'

const getInitialState = (): PaymentsReviewState => {
  return {
    pending: [],
    pendingLoading: false,
    pendingError: null,
    detailsEarningId: null,
    details: null,
    detailsLoading: false,
    detailsError: null,
    submitting: false,
    bulkProgress: null,
  }
}

export const paymentsReviewReducer = (
  state: PaymentsReviewState = getInitialState(),
  action: Action<unknown>,
): PaymentsReviewState => {
  switch (action.type) {
    // The queue is kept on refetch so the grid does not blank after every review.
    case AdminFetchPendingPayments.type.REQUEST:
      return { ...state, pendingLoading: true, pendingError: null }
    case AdminFetchPendingPayments.type.SUCCESS:
      return {
        ...state,
        pending: (action.payload as PendingPayment[] | undefined) ?? [],
        pendingLoading: false,
        pendingError: null,
      }
    case AdminFetchPendingPayments.type.FAILED:
      return {
        ...state,
        pendingLoading: false,
        pendingError: resolveErrorMessage(action.payload, 'Failed to load pending payments'),
      }

    case AdminFetchPendingPaymentDetails.type.REQUEST: {
      const earningId =
        (action.payload as FetchPendingPaymentDetailsPayload | undefined)?.earningId ?? null
      const isSameEarning = earningId === state.detailsEarningId

      return {
        ...state,
        detailsEarningId: earningId,
        details: isSameEarning ? state.details : null,
        detailsLoading: true,
        detailsError: null,
      }
    }
    case AdminFetchPendingPaymentDetails.type.SUCCESS:
      return {
        ...state,
        details: (action.payload as PendingPaymentDetails | undefined) ?? null,
        detailsLoading: false,
        detailsError: null,
      }
    case AdminFetchPendingPaymentDetails.type.FAILED:
      return {
        ...state,
        detailsLoading: false,
        detailsError: resolveErrorMessage(action.payload, 'Failed to load the payment'),
      }

    case AdminReviewPayment.type.REQUEST:
      return { ...state, submitting: true }
    case AdminBulkApprovePayments.type.REQUEST: {
      const earningIds =
        (action.payload as BulkApprovePaymentsPayload | undefined)?.earningIds ?? []

      return { ...state, submitting: true, bulkProgress: { done: 0, total: earningIds.length } }
    }
    case AdminBulkApprovePaymentsProgress.type:
      return {
        ...state,
        bulkProgress: (action.payload as BulkApproveProgress | undefined) ?? state.bulkProgress,
      }
    case AdminReviewPayment.type.SUCCESS:
    case AdminReviewPayment.type.FAILED:
      return { ...state, submitting: false }
    case AdminBulkApprovePayments.type.SUCCESS:
    case AdminBulkApprovePayments.type.FAILED:
      return { ...state, submitting: false, bulkProgress: null }

    default:
      return state
  }
}
